import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Card, Spin, Table, Tag } from '@/shared/ui/primitives';
import { useGrowthAnalytics } from '../hooks/useAnalyticsQueries';
import type { GrowthAnalyticsParams } from '../api/analytics.api';
import AnalyticsFilterBar from '../components/AnalyticsFilterBar';

type Section = 'overview' | 'funnels' | 'search' | 'marketplace' | 'attribution' | 'retention';

const SECTION_TITLES: Record<Section, string> = {
  overview: 'Growth Analytics',
  funnels: 'Funnel Analytics',
  search: 'Search Analytics',
  marketplace: 'Marketplace Analytics',
  attribution: 'Attribution Analytics',
  retention: 'Retention Analytics',
};

const SECTION_TITLE_KEYS: Record<Section, string> = {
  overview: 'analytics.growth.title',
  funnels: 'analytics.funnels.title',
  search: 'analytics.search.title',
  marketplace: 'analytics.marketplace.title',
  attribution: 'analytics.attribution.title',
  retention: 'analytics.retention.title',
};

function defaultFrom() {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().slice(0, 10);
}

function Stat({ title, value, hint }: { title: string; value: any; hint?: string }) {
  return (
    <div className="ds-stat">
      <div className="ds-stat-head"><span>{title}</span></div>
      <div className="ds-stat-value">{value ?? 0}</div>
      {hint && <div className="ds-stat-hint">{hint}</div>}
    </div>
  );
}

function MetricGrid({ children }: { children: React.ReactNode }) {
  return <div className="ds-row" style={{ gap: 16, marginInline: -8 }}>{children}</div>;
}

function MetricCell({ children }: { children: React.ReactNode }) {
  return <div style={{ flex: '1 1 220px', padding: 8 }}>{children}</div>;
}

function pct(value: any) {
  const n = typeof value === 'number' ? value : Number(value ?? 0);
  return `${Math.round(n * 1000) / 10}%`;
}

function countMap(data: any, key: string) {
  const value = data?.totals?.[key] ?? data?.[key] ?? 0;
  return Number(value) || 0;
}

function localizedDistrict(row: any) {
  const value = String(row?.districtName ?? '').trim();
  if (value && !/^\d+$/.test(value)) return value;
  return '';
}

function localizedValue(value: any, fallback: string) {
  const text = String(value ?? '').trim();
  return !text || text.toLowerCase() === 'unknown' ? fallback : text;
}

function mappedDistrictRows(rows?: any[]) {
  return (Array.isArray(rows) ? rows : []).filter((row) => row?.districtName);
}

function valueOf(row: any, key: string) {
  return Number(row?.[key] ?? 0);
}

function BusinessListCard({ title, rows, metric, emptyText }: { title: string; rows: any[]; metric: (row: any) => string; emptyText: string }) {
  return (
    <Card title={title} style={{ flex: '1 1 360px' }}>
      {rows.length === 0 ? (
        <div className="ds-muted">{emptyText}</div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {rows.slice(0, 6).map((row, index) => (
            <div key={`${title}-${row.districtName}-${index}`} style={{ display: 'grid', gridTemplateColumns: '32px 1fr auto', gap: 10, alignItems: 'center' }}>
              <strong style={{ color: 'var(--ds-primary)' }}>{index + 1}</strong>
              <div>
                <strong>{row.districtName}</strong>
                {row.cityName ? <div className="ds-muted">{row.cityName}</div> : null}
              </div>
              <strong>{metric(row)}</strong>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function SimpleTable({ rows, columns, rowKey = 'name', emptyText }: { rows?: any[]; columns: any[]; rowKey?: string; emptyText?: string }) {
  return (
    <Table
      size="small"
      rowKey={(row: any) => String(row?.[rowKey] ?? row?.eventName ?? row?.day ?? JSON.stringify(row))}
      dataSource={Array.isArray(rows) ? rows : []}
      columns={columns}
      pagination={false}
      locale={{ emptyText: emptyText ?? 'No analytics rows in this range' }}
      scroll={{ x: 720 }}
    />
  );
}

function InsightGrid({ insights }: { insights: Array<{ title: string; body: string; tone?: string }> }) {
  return (
    <div className="ds-row" style={{ gap: 12, marginTop: 16 }}>
      {insights.map((insight, index) => (
        <Card key={`${insight.title}-${index}`} styles={{ body: { padding: 14 } }} style={{ flex: '1 1 260px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'start' }}>
            <strong>{insight.title}</strong>
            {insight.tone ? <Tag color={insight.tone}>{insight.tone}</Tag> : null}
          </div>
          <div className="ds-muted" style={{ marginTop: 8, lineHeight: 1.5 }}>{insight.body}</div>
        </Card>
      ))}
    </div>
  );
}

function eventCount(rows: any[] = [], eventName: string) {
  const row = rows.find((item) => item?.eventName === eventName);
  return Number(row?.count ?? 0);
}

function Overview({ data }: { data: any }) {
  const { t } = useTranslation();
  const active = data?.activeUsers ?? {};
  const auth = data?.funnels?.auth ?? [];
  const search = data?.search ?? {};
  const marketplace = data?.marketplace ?? {};
	  const activeUsers = Number(active.active_users ?? active.activeUsers ?? 0);
	  const sessions = Number(active.sessions ?? 0);
	  const zeroRate = Number(search.zeroResultRate ?? 0);
	  const contacts = countMap(marketplace, 'contact_completed');
	  const published = countMap(marketplace, 'listing_room_ad_published') + countMap(marketplace, 'listing_room_wanted_published');
  return (
    <>
      <InsightGrid insights={[
        {
	          title: t('analytics.insights.activityTitle'),
	          body: activeUsers > 0
	            ? t('analytics.insights.activityBody', { users: activeUsers.toLocaleString(), sessions: sessions.toLocaleString() })
	            : t('analytics.insights.noActivityBody'),
	        },
        {
          title: t('analytics.insights.searchHealthTitle'),
          body: zeroRate > 0.2
            ? t('analytics.insights.searchHealthHighZero', { rate: pct(zeroRate) })
            : t('analytics.insights.searchHealthHealthy', { rate: pct(zeroRate) }),
        },
        {
          title: t('analytics.insights.marketplaceTitle'),
          body: t('analytics.insights.marketplaceBody', { contacts, published }),
        },
      ]} />
      <MetricGrid>
        <MetricCell><Stat title={t('analytics.metrics.activeUsers')} value={active.active_users ?? active.activeUsers} /></MetricCell>
        <MetricCell><Stat title={t('analytics.metrics.authenticatedUsers')} value={active.authenticated_users ?? active.authenticatedUsers} /></MetricCell>
        <MetricCell><Stat title={t('analytics.metrics.sessions')} value={active.sessions} /></MetricCell>
        <MetricCell><Stat title={t('analytics.metrics.searchZeroRate')} value={pct(search.zeroResultRate)} /></MetricCell>
      </MetricGrid>
      <Card title={t('analytics.marketplace.snapshot')} style={{ marginTop: 16 }}>
        <MetricGrid>
          <MetricCell><Stat title={t('analytics.metrics.contactsCompleted')} value={countMap(marketplace, 'contact_completed')} /></MetricCell>
          <MetricCell><Stat title={t('analytics.metrics.roomAdsPublished')} value={countMap(marketplace, 'listing_room_ad_published')} /></MetricCell>
          <MetricCell><Stat title={t('analytics.metrics.wantedAdsPublished')} value={countMap(marketplace, 'listing_room_wanted_published')} /></MetricCell>
        </MetricGrid>
      </Card>
      <details style={{ marginTop: 18 }}>
        <summary style={{ cursor: 'pointer', fontWeight: 700 }}>{t('analytics.common.advancedDebug')}</summary>
        <Card title={t('analytics.funnels.auth')} style={{ marginTop: 12 }}>
          <SimpleTable rows={auth} rowKey="eventName" columns={[
            { title: t('analytics.columns.event'), dataIndex: 'eventName' },
            { title: t('analytics.columns.events'), dataIndex: 'count' },
            { title: t('analytics.columns.users'), dataIndex: 'users' },
          ]} emptyText={t('analytics.empty')} />
        </Card>
      </details>
    </>
  );
}

function Funnels({ data }: { data: any }) {
  const { t } = useTranslation();
  const auth = data?.auth ?? [];
  const listing = data?.listing ?? [];
	  const contact = data?.contact ?? [];
	  const otpRequested = eventCount(auth, 'auth_otp_requested');
	  const otpVerified = eventCount(auth, 'auth_otp_verified');
	  const signupCompleted = eventCount(auth, 'auth_signup_completed');
  const publishStarted = eventCount(listing, 'listing_room_ad_publish_started') + eventCount(listing, 'listing_room_wanted_publish_started');
  const published = eventCount(listing, 'listing_room_ad_published') + eventCount(listing, 'listing_room_wanted_published');
  const contactAttempts = eventCount(contact, 'contact_attempted');
  const contactCompleted = eventCount(contact, 'contact_completed');
  const render = (title: string, rows: any[]) => (
    <Card title={title} style={{ marginBottom: 16 }}>
      <SimpleTable rows={rows} rowKey="eventName" columns={[
        { title: t('analytics.columns.step'), dataIndex: 'label' },
        { title: t('analytics.columns.event'), dataIndex: 'eventName' },
        { title: t('analytics.columns.count'), dataIndex: 'count' },
        { title: t('analytics.columns.fromFirst'), render: (_: any, row: any) => pct(row.conversionFromFirst) },
        { title: t('analytics.columns.fromPrevious'), render: (_: any, row: any) => pct(row.conversionFromPrevious) },
      ]} emptyText={t('analytics.empty')} />
    </Card>
  );
  return (
    <>
      <InsightGrid insights={[
	        {
	          title: t('analytics.funnels.auth'),
	          body: t('analytics.insights.authFunnelBody', { verified: otpVerified || otpRequested, signups: signupCompleted }),
	        },
        {
          title: t('analytics.funnels.listing'),
          body: t('analytics.insights.listingFunnelBody', { started: publishStarted, published }),
        },
	        {
	          title: t('analytics.funnels.contact'),
	          body: t('analytics.insights.contactFunnelBody', { attempted: contactAttempts, completed: contactCompleted }),
	        },
      ]} />
      <MetricGrid>
        <MetricCell><Stat title={t('analytics.metrics.signupCompleted')} value={signupCompleted} /></MetricCell>
        <MetricCell><Stat title={t('analytics.metrics.publishCompleted')} value={published} /></MetricCell>
        <MetricCell><Stat title={t('analytics.metrics.contactsCompleted')} value={contactCompleted} /></MetricCell>
      </MetricGrid>
      <details style={{ marginTop: 18 }}>
        <summary style={{ cursor: 'pointer', fontWeight: 700 }}>{t('analytics.common.advancedDebug')}</summary>
        <div style={{ marginTop: 12 }}>
          {render(t('analytics.funnels.auth'), auth)}
          {render(t('analytics.funnels.listing'), listing)}
          {render(t('analytics.funnels.contact'), contact)}
        </div>
      </details>
    </>
  );
}

function SearchAnalytics({ data }: { data: any }) {
  const { t } = useTranslation();
  const districtRows = mappedDistrictRows(data?.districtDemand);
  const mostSearched = [...districtRows]
    .filter((row) => valueOf(row, 'searches') > 0)
    .sort((a, b) => valueOf(b, 'searches') - valueOf(a, 'searches'));
  const zeroHotspots = [...districtRows]
    .filter((row) => valueOf(row, 'zeroResults') > 0)
    .sort((a, b) => valueOf(b, 'zeroResults') - valueOf(a, 'zeroResults'));
	  const topDistrictRow = mostSearched[0];
	  const topDistrict = topDistrictRow?.districtName;
  const zeroRate = Number(data?.zeroResultRate ?? 0);
  return (
    <>
      <InsightGrid insights={[
        {
	          title: t('analytics.search.topDemandTitle'),
	          body: topDistrict ? t('analytics.search.topDemandBody', { district: topDistrict, count: valueOf(topDistrictRow, 'searches') }) : t('analytics.search.noDemandInsight'),
        },
        {
          title: t('analytics.search.searchQualityTitle'),
          body: zeroRate > 0.2
            ? t('analytics.search.highZeroRateBody', { rate: pct(zeroRate) })
            : t('analytics.search.healthyZeroRateBody', { rate: pct(zeroRate) }),
        },
      ]} />
      <MetricGrid>
        <MetricCell><Stat title={t('analytics.metrics.searches')} value={countMap(data, 'search_submitted')} /></MetricCell>
        <MetricCell><Stat title={t('analytics.metrics.zeroResultSearches')} value={countMap(data, 'search_zero_results')} hint={pct(data?.zeroResultRate)} /></MetricCell>
        <MetricCell><Stat title={t('analytics.metrics.resultClicks')} value={countMap(data, 'search_result_clicked')} hint={pct(data?.clickThroughRate)} /></MetricCell>
        <MetricCell><Stat title={t('analytics.metrics.districtSelections')} value={countMap(data, 'district_selected')} /></MetricCell>
      </MetricGrid>
      <div className="ds-row" style={{ gap: 16, marginTop: 16 }}>
        <BusinessListCard
          title={t('analytics.search.mostSearchedDistricts')}
          rows={mostSearched}
          metric={(row) => t('analytics.demand.searchesCount', { count: valueOf(row, 'searches') })}
          emptyText={t('analytics.marketplace.noDemand')}
        />
        <BusinessListCard
          title={t('analytics.search.zeroResultHotspots')}
          rows={zeroHotspots}
          metric={(row) => t('analytics.demand.zeroResultSearchesCount', { count: valueOf(row, 'zeroResults') })}
          emptyText={t('analytics.marketplace.noFailures')}
        />
      </div>
      <details style={{ marginTop: 18 }}>
        <summary style={{ cursor: 'pointer', fontWeight: 700 }}>{t('analytics.search.advancedDetail')}</summary>
        <Card title={t('analytics.search.districtDemand')} style={{ marginTop: 12 }}>
          <SimpleTable rows={districtRows} rowKey="districtId" columns={[
            { title: t('common.district'), render: (_: any, row: any) => localizedDistrict(row) },
            { title: t('analytics.columns.searches'), dataIndex: 'searches' },
            { title: t('analytics.columns.zeroResults'), dataIndex: 'zeroResults' },
            { title: t('analytics.columns.events'), dataIndex: 'events' },
          ]} emptyText={t('analytics.empty')} />
        </Card>
        <Card title={t('analytics.search.nearbyFallback')} style={{ marginTop: 12 }}>
          <SimpleTable rows={data?.nearbyFallback} rowKey="value" columns={[
            { title: t('common.value'), render: (_: any, row: any) => localizedValue(row.value, t('common.notFound')) },
            { title: t('analytics.columns.count'), dataIndex: 'count' },
          ]} emptyText={t('analytics.empty')} />
        </Card>
      </details>
    </>
  );
}

function Marketplace({ data }: { data: any }) {
  const { t } = useTranslation();
  const districtRows = mappedDistrictRows(data?.districtSupplyDemand)
    .filter((row) => valueOf(row, 'events') > 0 || valueOf(row, 'searches') > 0 || valueOf(row, 'published') > 0 || valueOf(row, 'contacts') > 0);
  const mostDemanded = [...districtRows]
    .filter((row) => valueOf(row, 'searches') > 0)
    .sort((a, b) => valueOf(b, 'searches') - valueOf(a, 'searches'));
  const supplyShortage = [...districtRows]
    .filter((row) => valueOf(row, 'searches') > 0 && valueOf(row, 'published') <= 2)
    .sort((a, b) => (valueOf(b, 'searches') / Math.max(1, valueOf(b, 'published'))) - (valueOf(a, 'searches') / Math.max(1, valueOf(a, 'published'))));
  const searchFailures = [...districtRows]
    .filter((row) => valueOf(row, 'zeroResults') > 0)
    .sort((a, b) => valueOf(b, 'zeroResults') - valueOf(a, 'zeroResults'));
  const bestConversion = [...districtRows]
    .filter((row) => valueOf(row, 'contacts') > 0)
    .sort((a, b) => (valueOf(b, 'contacts') / Math.max(1, valueOf(b, 'searches'))) - (valueOf(a, 'contacts') / Math.max(1, valueOf(a, 'searches'))));
  const recommendations = [
    ...supplyShortage.slice(0, 2).map((row) => t('analytics.marketplace.recommendSupply', { district: row.districtName })),
    ...searchFailures.slice(0, 2).map((row) => t('analytics.marketplace.recommendFailure', { district: row.districtName })),
    ...bestConversion.slice(0, 1).map((row) => t('analytics.marketplace.recommendWinner', { district: row.districtName })),
  ].slice(0, 5);
  return (
    <>
      <InsightGrid insights={[
        {
          title: t('analytics.marketplace.supplyShortage'),
          body: supplyShortage[0]
            ? t('analytics.insights.supplyShortageBody', { district: supplyShortage[0].districtName })
            : t('analytics.marketplace.noShortage'),
        },
        {
	          title: t('analytics.marketplace.bestConverting'),
	          body: bestConversion[0]
	            ? t('analytics.insights.bestConversionBody', { district: bestConversion[0].districtName, contacts: valueOf(bestConversion[0], 'contacts') })
	            : t('analytics.marketplace.noConversions'),
        },
      ]} />
      <Card title={t('analytics.marketplace.whatToDo')}>
        {recommendations.length === 0 ? (
          <div className="ds-muted">{t('analytics.marketplace.noRecommendations')}</div>
        ) : (
          <div className="ds-row" style={{ gap: 12 }}>
            {recommendations.map((message, index) => (
              <Card key={`${message}-${index}`} styles={{ body: { padding: 14 } }} style={{ flex: '1 1 280px' }}>
                <strong>{message}</strong>
              </Card>
            ))}
          </div>
        )}
      </Card>
      <MetricGrid>
        <MetricCell><Stat title={t('analytics.metrics.roomAdPublishConversion')} value={pct(data?.publishConversion?.roomAds)} /></MetricCell>
        <MetricCell><Stat title={t('analytics.metrics.wantedPublishConversion')} value={pct(data?.publishConversion?.roomWanted)} /></MetricCell>
        <MetricCell><Stat title={t('analytics.metrics.contactConversion')} value={pct(data?.contactConversion)} /></MetricCell>
        <MetricCell><Stat title={t('analytics.metrics.conversationsStarted')} value={countMap(data, 'chat_conversation_started')} /></MetricCell>
      </MetricGrid>
      <div className="ds-row" style={{ gap: 16, marginTop: 16 }}>
        <BusinessListCard title={t('analytics.marketplace.mostDemanded')} rows={mostDemanded} metric={(row) => t('analytics.demand.searchesCount', { count: valueOf(row, 'searches') })} emptyText={t('analytics.marketplace.noDemand')} />
        <BusinessListCard title={t('analytics.marketplace.supplyShortage')} rows={supplyShortage} metric={(row) => t('analytics.marketplace.supplyMetric', { searches: valueOf(row, 'searches'), published: valueOf(row, 'published') })} emptyText={t('analytics.marketplace.noShortage')} />
      </div>
      <div className="ds-row" style={{ gap: 16, marginTop: 16 }}>
        <BusinessListCard title={t('analytics.marketplace.searchFailureHotspots')} rows={searchFailures} metric={(row) => t('analytics.demand.zeroResultSearchesCount', { count: valueOf(row, 'zeroResults') })} emptyText={t('analytics.marketplace.noFailures')} />
        <BusinessListCard title={t('analytics.marketplace.bestConverting')} rows={bestConversion} metric={(row) => t('analytics.marketplace.contactsMetric', { count: valueOf(row, 'contacts') })} emptyText={t('analytics.marketplace.noConversions')} />
      </div>
      <details style={{ marginTop: 18 }}>
        <summary style={{ cursor: 'pointer', fontWeight: 700 }}>{t('analytics.marketplace.advancedDetail')}</summary>
        <Card title={t('analytics.marketplace.districtSupplyDemand')} style={{ marginTop: 12 }}>
          <SimpleTable rows={districtRows} rowKey="districtId" columns={[
            { title: t('common.district'), render: (_: any, row: any) => localizedDistrict(row) },
            { title: t('analytics.columns.searches'), dataIndex: 'searches' },
            { title: t('analytics.columns.zeroResults'), dataIndex: 'zeroResults' },
            { title: t('analytics.columns.published'), dataIndex: 'published' },
            { title: t('analytics.columns.contacts'), dataIndex: 'contacts' },
          ]} emptyText={t('analytics.empty')} />
        </Card>
        <Card title={t('analytics.marketplace.contactMethods')} style={{ marginTop: 12 }}>
          <SimpleTable rows={data?.contactMethods} rowKey="value" columns={[
            { title: t('analytics.columns.method'), render: (_: any, row: any) => localizedValue(row.value, t('common.notFound')) },
            { title: t('analytics.columns.count'), dataIndex: 'count' },
          ]} emptyText={t('analytics.empty')} />
        </Card>
      </details>
    </>
  );
}

function Attribution({ data }: { data: any }) {
  const { t } = useTranslation();
  const campaigns = Array.isArray(data?.campaigns) ? data.campaigns : [];
  const topCampaigns = [...campaigns]
    .filter((row) => Number(row?.events ?? 0) > 0)
    .sort((a, b) => Number(b.contacts ?? 0) + Number(b.published ?? 0) + Number(b.signups ?? 0) - (Number(a.contacts ?? 0) + Number(a.published ?? 0) + Number(a.signups ?? 0)));
  const topCampaign = topCampaigns[0];
  return (
    <>
      <InsightGrid insights={[
        {
          title: t('analytics.attribution.bestCampaign'),
          body: topCampaign ? t('analytics.attribution.bestCampaignBody', { campaign: localizedValue(topCampaign.name, t('common.notFound')) }) : t('analytics.attribution.noCampaignInsight'),
        },
        {
          title: t('analytics.attribution.attributionHealth'),
          body: t('analytics.attribution.attributionHealthBody', { count: Number(data?.recentAttributions?.length ?? 0) }),
        },
      ]} />
      <Card title={t('analytics.attribution.campaignPerformance')} style={{ marginTop: 16 }}>
        {topCampaigns.length === 0 ? (
          <div className="ds-muted">{t('analytics.empty')}</div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {topCampaigns.slice(0, 6).map((row, index) => (
              <div key={`${row.name}-${index}`} style={{ display: 'grid', gridTemplateColumns: '32px 1fr auto', gap: 10, alignItems: 'center' }}>
                <strong style={{ color: 'var(--ds-primary)' }}>{index + 1}</strong>
                <div>
                  <strong>{localizedValue(row.name, t('common.notFound'))}</strong>
                  <div className="ds-muted">{t('analytics.attribution.campaignResult', { signups: Number(row.signups ?? 0), published: Number(row.published ?? 0), contacts: Number(row.contacts ?? 0) })}</div>
                </div>
                <strong>{Number(row.events ?? 0)}</strong>
              </div>
            ))}
          </div>
        )}
      </Card>
      <details style={{ marginTop: 18 }}>
        <summary style={{ cursor: 'pointer', fontWeight: 700 }}>{t('analytics.common.advancedDebug')}</summary>
        <Card title={t('analytics.attribution.recentAppsFlyer')} style={{ marginTop: 12 }}>
          <SimpleTable rows={data?.recentAttributions} rowKey="appsflyerId" columns={[
            { title: 'AppsFlyer ID', dataIndex: 'appsflyerId' },
            { title: t('common.user'), dataIndex: 'linkedUserId' },
            { title: t('analytics.columns.source'), dataIndex: 'mediaSource' },
            { title: t('analytics.columns.campaign'), dataIndex: 'campaign' },
            { title: t('analytics.columns.deepLink'), dataIndex: 'deepLinkValue' },
            { title: t('analytics.columns.retargeting'), render: (_: any, row: any) => row.isRetargeting ? <Tag color="info">{t('common.yes')}</Tag> : '-' },
            { title: t('analytics.columns.events'), dataIndex: 'events' },
          ]} emptyText={t('analytics.empty')} />
        </Card>
        <Card title={t('analytics.attribution.verificationEvents')} style={{ marginTop: 12 }}>
          <SimpleTable rows={data?.verificationEvents} rowKey="id" columns={[
            { title: t('analytics.columns.event'), dataIndex: 'eventName' },
            { title: t('common.user'), dataIndex: 'userId' },
            { title: t('analytics.columns.anonymous'), dataIndex: 'anonymousId' },
            { title: t('analytics.columns.source'), dataIndex: 'mediaSource' },
            { title: t('analytics.columns.campaign'), dataIndex: 'campaign' },
            { title: t('common.createdAt'), dataIndex: 'createdAt' },
          ]} emptyText={t('analytics.empty')} />
        </Card>
      </details>
    </>
  );
}

function Retention({ data }: { data: any }) {
  const { t } = useTranslation();
  const summary = data?.summary ?? {};
  const activeUsers = Number(summary.active_users ?? summary.activeUsers ?? 0);
  const sessions = Number(summary.sessions ?? 0);
  return (
    <>
	      <InsightGrid insights={[
	        {
	          title: t('analytics.retention.returningUsersTitle'),
	          body: activeUsers > 0 ? t('analytics.retention.returningUsersBody', { count: activeUsers, users: activeUsers, sessions }) : t('analytics.retention.noRetentionInsight'),
	        },
	        {
	          title: t('analytics.retention.engagementTitle'),
	          body: t('analytics.retention.engagementBody', { value: Number(data?.sessionFrequency?.averageEventsPerSession ?? 0).toFixed(1) }),
	        },
      ]} />
      <MetricGrid>
        <MetricCell><Stat title={t('analytics.metrics.activeUsers')} value={summary.active_users ?? summary.activeUsers} /></MetricCell>
        <MetricCell><Stat title={t('analytics.metrics.sessions')} value={summary.sessions} /></MetricCell>
        <MetricCell><Stat title={t('analytics.metrics.avgSessionSeconds')} value={Math.round(Number(data?.sessionFrequency?.averageDurationSeconds ?? 0))} /></MetricCell>
        <MetricCell><Stat title={t('analytics.metrics.avgEventsSession')} value={Math.round(Number(data?.sessionFrequency?.averageEventsPerSession ?? 0) * 10) / 10} /></MetricCell>
      </MetricGrid>
      <details style={{ marginTop: 18 }}>
        <summary style={{ cursor: 'pointer', fontWeight: 700 }}>{t('analytics.common.advancedDebug')}</summary>
        <Card title={t('analytics.retention.dailyActiveUsers')} style={{ marginTop: 12 }}>
          <SimpleTable rows={data?.dailyActiveUsers} rowKey="day" columns={[
            { title: t('analytics.columns.day'), dataIndex: 'day' },
            { title: t('analytics.metrics.activeUsers'), dataIndex: 'activeUsers' },
            { title: t('analytics.metrics.authenticatedUsers'), dataIndex: 'authenticatedUsers' },
            { title: t('analytics.metrics.sessions'), dataIndex: 'sessions' },
          ]} emptyText={t('analytics.empty')} />
        </Card>
        <Card title={t('analytics.retention.cohortFoundation')} style={{ marginTop: 12 }}>
          <SimpleTable rows={data?.cohorts} rowKey="cohortDay" columns={[
            { title: t('analytics.columns.cohortDay'), dataIndex: 'cohortDay' },
            { title: t('analytics.columns.users'), dataIndex: 'cohortSize' },
            { title: 'D1', dataIndex: 'day1' },
            { title: 'D7', dataIndex: 'day7' },
            { title: 'D30', dataIndex: 'day30' },
          ]} emptyText={t('analytics.empty')} />
        </Card>
      </details>
    </>
  );
}

export default function GrowthAnalyticsPage({ section }: { section: Section }) {
  const { t } = useTranslation();
  const [params, setParams] = useState<GrowthAnalyticsParams>({ from: defaultFrom() });
  const normalizedParams = useMemo(() => ({
    ...params,
    from: params.from ? `${params.from}T00:00:00Z` : undefined,
    to: params.to ? `${params.to}T23:59:59Z` : undefined,
  }), [params]);
  const { data, isLoading, isFetching, isError, refetch } = useGrowthAnalytics(section, normalizedParams);

  const content = () => {
    if (isLoading) return <Spin />;
    if (isError) return <Alert type="error" showIcon message={t('analytics.failedLoad')} />;
    switch (section) {
      case 'overview': return <Overview data={data} />;
      case 'funnels': return <Funnels data={data} />;
      case 'search': return <SearchAnalytics data={data} />;
      case 'marketplace': return <Marketplace data={data} />;
      case 'attribution': return <Attribution data={data} />;
      case 'retention': return <Retention data={data} />;
    }
  };

  return (
    <div>
      <div className="ds-page-header">
        <div>
          <h1>{t(SECTION_TITLE_KEYS[section], SECTION_TITLES[section])}</h1>
          <div className="ds-page-sub">{t('analytics.subtitle')}</div>
        </div>
        {isFetching && !isLoading ? <Tag color="info">{t('analytics.refreshing')}</Tag> : null}
      </div>
      <AnalyticsFilterBar params={params} setParams={setParams} onRefresh={() => refetch()} loading={isFetching} />
      {content()}
    </div>
  );
}
